import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sparkles,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Japan DMC", path: "/japan-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE",
    copy:
      "Japan sets the global benchmark for MICE execution — and Trawelldmc brings this world-class destination within reach for Indian and Asian corporate groups. Our Japan MICE programmes are crafted for incentive travel, leadership conferences, and premium reward trips, with programmes anchored in Tokyo, Kyoto, Osaka, and Hokkaido. We manage group visas, airline block space, luxury hotel contracting (Park Hyatt, Aman, Four Seasons), themed gala dinners in traditional ryokans, Shinkansen group bookings, and exclusive experiences such as private tea ceremonies, sumo stables visits, and sake brewery tours. Japan MICE with Trawelldmc is precision-planned and impeccably delivered.",
  },
  {
    icon: Briefcase,
    title: "Group Leisure",
    copy:
      "Japan's extraordinary range of landscapes, seasons, and cultural experiences makes it one of the most rewarding group leisure destinations in the world. We design fully escorted Japan group programmes covering Tokyo's urban energy, the timeless temples of Kyoto and Nara, Osaka's legendary food culture, and scenic highlights including Mt. Fuji, Hakone, and Hiroshima. Seasonal themes — cherry blossom (March–April) and autumn foliage (October–November) — are available as special group departures. Programmes range from 7N/8D to 12N/13D, with Japanese-speaking local guides, premium coaches, and a mix of western and traditional hotel options including ryokan stays.",
  },
  {
    icon: Sparkles,
    title: "FIT",
    copy:
      "Japan rewards the independent traveller with a lifetime of discovery — and our FIT desk makes navigating this extraordinary country effortless. Our Japan FIT programmes are tailored to every style: first-timers on a classic Tokyo-Kyoto-Osaka circuit, repeat travellers exploring Hokkaido, Kanazawa, or Okinawa, and luxury seekers combining Aman ryokans with private cultural experiences. All FIT packages include Shinkansen passes, private airport transfers, pre-booked hotels, pocket Wi-Fi, and a curated day-by-day itinerary. Group visa documentation support is provided for Indian passport holders as part of every booking.",
  },
];

const HIGHLIGHTS = [
  "Tokyo city incentives",
  "Kyoto temple experiences",
  "Shinkansen bullet train journeys",
  "Mt. Fuji & Hakone day trips",
  "Osaka team-building & food culture",
  "Cherry blossom / autumn foliage seasons",
];

const VENUES = [
  { name: "Park Hyatt Tokyo", note: "Shinjuku icon — premium leadership retreats" },
  { name: "Aman Tokyo / Aman Kyoto", note: "Luxury group buyouts in ultra-private settings" },
  { name: "Four Seasons Hotel Tokyo at Otemachi", note: "Modern flagship near Imperial Palace" },
  { name: "The Ritz-Carlton, Osaka", note: "Western Japan's leading 5-star MICE hotel" },
  { name: "Hoshinoya Karuizawa", note: "Signature ryokan experiences for senior incentive groups" },
  { name: "Tokyo Big Sight & Makuhari Messe", note: "Japan's largest convention complexes" },
];

const FAQS = [
  {
    q: "How early should we plan a Japan MICE programme?",
    a: "We recommend 9–12 months of lead time for groups of 200+ pax, especially during peak seasons (cherry blossom in March–April; autumn foliage in October–November). Hotel inventory in Tokyo and Kyoto is the binding constraint — early commitment unlocks the best rates and venue options.",
  },
  {
    q: "Is Japan affordable for Indian and Middle Eastern corporate groups?",
    a: "Japan is a premium destination, but Trawelldmc has long-standing rates with Japanese hotel chains, internal-flight partners, and Shinkansen suppliers that materially reduce landed cost. We typically deliver Japan programmes within 15–25% of a comparable Europe incentive — for content that is far more differentiated.",
  },
  {
    q: "Do you handle Japan visa documentation for Indian passports?",
    a: "Yes. Japan visa documentation is one of the more demanding parts of operating an Indian group, and we manage the entire process — invitation letters, financial guarantor docs, itinerary submissions, and consulate appointments — through our partner visa house.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function JapanDmc() {
  return (
    <div data-testid="japan-dmc-page">
      <Seo
        path="/japan-dmc"
        title="Japan DMC | Incentive Travel & MICE | Trawelldmc"
        description="DMC in Japan — Tokyo, Kyoto, Osaka & beyond. Premium MICE, incentive, group leisure & FIT programmes. Request a Japan proposal."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.japan}
          alt="Tokyo & Mount Fuji — Trawelldmc DMC Japan"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Japan DMC · Tokyo · Kyoto · Osaka
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Japan</span> Destination Management Company (DMC) & Premium MICE Specialist
            </h1>
            <p className="text-white/80 mt-6 text-sm tracking-[0.2em] uppercase font-medium">
              Tradition Meets Innovation
            </p>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Precision-planned MICE, incentive, group leisure and FIT
              programmes across Japan — delivered with the punctuality and
              perfection the destination is known for.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Japan"
                className="btn-primary"
                data-testid="jp-cta-proposal"
              >
                Get a Japan proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="jp-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY JAPAN / OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Japan</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              The benchmark for <br />incentive precision.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Japan is a destination unlike any other — a seamless blend of
              ancient tradition, contemporary innovation, and legendary
              hospitality. Trawelldmc's Japan programmes are designed for
              corporates and incentive groups who demand precision, elegance,
              and memorable moments.
            </p>
            <p>
              From high-speed Shinkansen journeys and private temple
              experiences in Kyoto to cutting-edge teambuilding in Tokyo and
              iconic Mt. Fuji excursions, we craft itineraries that reflect
              Japan's extraordinary range. With our trusted local partners and
              multilingual support, every Japan programme runs with the
              punctuality and perfection the destination is known for.
            </p>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Top Experiences</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Japan highlights.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h} className="card-flat p-6 flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-[#0B192C] font-medium">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Japan services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8" data-testid={`jp-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`jp-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Japan programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Japan proposal. We respond within
              24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Japan"
              className="btn-primary w-full justify-center"
              data-testid="jp-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/hong-kong-dmc" className="group flex items-start gap-3" data-testid="jp-link-hk">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Hong Kong</div>
                <div className="text-sm text-slate-500 mt-1">Harbour events & galas</div>
              </div>
            </Link>
            <Link to="/singapore-dmc" className="group flex items-start gap-3" data-testid="jp-link-singapore">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Singapore</div>
                <div className="text-sm text-slate-500 mt-1">MICE capital of Asia</div>
              </div>
            </Link>
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="jp-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="jp-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
